// Scoped-document store — bridges the app's scoped localStorage keys to the
// server. On boot we hydrate localStorage from the server (loadMine); thereafter
// a localStorage write-through (installed in App.js) mirrors every change back
// via put()/remove(). This keeps the existing cascade/data code untouched.

import { api } from '../api';
import * as syncState from './syncState';

// In-flight write-through PUTs, so a discrete action can confirm its save landed.
const _inflight = new Set();

export const docStore = {
  // { "<base>__<scope>": data, … } for every doc the signed-in user may read.
  async loadMine() {
    const { docs } = await api.get('/api/store/mine');
    return docs || {};
  },
  // Load docs for specific scopes only (no whole-tree reconcile). Used right after
  // creating an MLS/org, whose scopes were just seeded server-side.
  async loadScopes(scopes) {
    const ids = (scopes || []).filter(Boolean).join(',');
    if (!ids) return {};
    const { docs } = await api.get(`/api/store/by-scope?ids=${encodeURIComponent(ids)}`);
    return docs || {};
  },
  // Mirror one localStorage key's value to the server (value is the raw string).
  put(key, rawValue) {
    let data;
    try { data = JSON.parse(rawValue); } catch { data = rawValue; }
    const p = api.put('/api/store', { key, data });
    _inflight.add(p);
    p.then(() => _inflight.delete(p), () => _inflight.delete(p));
    return p;
  },
  remove(key) {
    const p = api.delete(`/api/store/${encodeURIComponent(key)}`);
    _inflight.add(p);
    p.then(() => _inflight.delete(p), () => _inflight.delete(p));
    return p;
  },
  // Resolve once all in-flight writes have settled — lets a discrete action (e.g.
  // duplicating a template) CONFIRM its save reached the DB before telling the user,
  // so a quick refresh can't make a just-saved item look lost.
  async flush() {
    await Promise.allSettled([..._inflight]);
    return syncState.flush();   // resolves true if everything drained, false if writes are still stuck
  },
  // Copy one reminder down to lower scopes as independent copies (server makes the
  // copies + renames on a name collision). Returns { pushed, total }.
  push(fromScope, reminderId, targets) {
    return api.post('/api/store/push', { fromScope, reminderId, targets });
  },
};

// Which localStorage keys are server-mirrored. Must match SCOPED_BASES / GLOBAL_KEYS
// in server/routes/store.ts.
// NOTE: contacts, properties, and the reminder-template keys (custom_templates,
// templates, autoenroll, attachments) are intentionally NOT here — they persist via an
// explicit AWAITED save (syncState.putScoped) in their components so the request can't
// be dropped on iOS. Only secondary keys still ride the fire-and-forget mirror.
const MIRRORED_BASES = [
  'ira_assignments', 'ira_run_history', 'ira_mls', 'ira_files_hidden',
];
// Non-scoped GLOBAL keys mirrored as singletons (must match GLOBAL_KEYS on the server).
// NOTE: ira_files is NOT mirrored anymore — the file library is server-authoritative
// via /api/files (bytes never touch localStorage; the server rejects mirror writes).
const MIRRORED_GLOBAL_KEYS = ['ira_process_interval_hours'];
export function isMirroredKey(key) {
  if (MIRRORED_GLOBAL_KEYS.includes(key)) return true;   // global shared library / system process interval
  const idx = key.lastIndexOf('__');
  if (idx === -1) return false;
  return MIRRORED_BASES.includes(key.slice(0, idx));
}
